import { translate } from '../../i18n/index.js';
import { type CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { membershipsApi, type MyMembership } from '../../api/memberships.api.js';
import { BackLink, SearchIcon, Spinner, Tag } from '../../components/design/AtomFitUI.js';
import { getRuntimeConfig } from '../../config/runtime.js';
import { useAuth } from '../../hooks/useAuth.js';
import { useGyms } from '../../hooks/useGyms.js';
import { useMembershipCheckoutStore } from '../../store/membership-checkout.store.js';
import type { Gym } from '@atomfit/shared';
import { MembershipTenantPicker } from '../../components/membership/MembershipTenantPicker.js';
import { GymInfoDisclosure } from '../../components/gyms/GymInfoDisclosure.js';

let googleMapsPromise: Promise<GoogleMapsApi> | null = null;
const SHOW_GYM_MAP = false;

interface GoogleMapsApi {
  Map: new (
    element: HTMLElement,
    options: {
      center: { lat: number; lng: number };
      zoom: number;
      disableDefaultUI?: boolean;
      mapTypeControl?: boolean;
      streetViewControl?: boolean;
      fullscreenControl?: boolean;
    },
  ) => GoogleMapInstance;
  Marker: new (options: {
    map: GoogleMapInstance;
    position: { lat: number; lng: number };
    title: string;
  }) => GoogleMapMarker;
  LatLngBounds: new () => GoogleLatLngBounds;
}

interface GoogleMapInstance {
  fitBounds: (bounds: GoogleLatLngBounds, padding?: number) => void;
  setCenter: (position: { lat: number; lng: number }) => void;
  setZoom: (zoom: number) => void;
}

interface GoogleMapMarker {
  setMap: (map: GoogleMapInstance | null) => void;
  addListener: (eventName: 'click', handler: () => void) => { remove: () => void };
}

interface GoogleLatLngBounds {
  extend: (position: { lat: number; lng: number }) => void;
}

export function GymFinder() {
  const [searchParams] = useSearchParams();
  return searchParams.get('flow') === 'membership' ? <MembershipTenantPicker /> : <GymBrowser />;
}

function GymBrowser() {
  const { gyms, loading, error } = useGyms();
  const { isAuthenticated } = useAuth();
  const pendingMembershipPlan = useMembershipCheckoutStore((state) => state.pendingPlan);
  const setPendingPlan = useMembershipCheckoutStore((state) => state.setPendingPlan);
  const [query, setQuery] = useState('');
  const [memberships, setMemberships] = useState<MyMembership[]>([]);
  const [membershipsLoading, setMembershipsLoading] = useState(isAuthenticated);
  useEffect(() => {
    if (pendingMembershipPlan) setPendingPlan(null);
  }, [pendingMembershipPlan, setPendingPlan]);

  useEffect(() => {
    if (!isAuthenticated) {
      setMemberships([]);
      setMembershipsLoading(false);
      return;
    }

    let cancelled = false;
    setMembershipsLoading(true);
    membershipsApi
      .my()
      .then((response) => {
        if (!cancelled) setMemberships(response.data.data);
      })
      .catch(() => {
        if (!cancelled) setMemberships([]);
      })
      .finally(() => {
        if (!cancelled) setMembershipsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const membershipGymIds = useMemo(
    () => new Set(memberships.flatMap((membership) => membership.tenant.gyms.map((gym) => gym.id))),
    [memberships],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = q
      ? gyms.filter((gym) =>
          `${gym.name} ${gym.city} ${gym.addressLine1}`.toLowerCase().includes(q),
        )
      : gyms;

    return [...matches].sort((first, second) => {
      const firstHasMembership = membershipGymIds.has(first.id);
      const secondHasMembership = membershipGymIds.has(second.id);
      if (firstHasMembership === secondHasMembership) return 0;
      return firstHasMembership ? -1 : 1;
    });
  }, [gyms, membershipGymIds, query]);

  return (
    <main className="af-screen">
      <section className="af-shell af-booking-flow-shell">
        <BackLink to="/">{translate('pages.public.gym_finder.gym_finder.back_link.back')}</BackLink>
        <h1 style={{ fontSize: 36, fontWeight: 800, letterSpacing: '-1px', margin: '0 0 6px' }}>
          {translate('pages.public.gym_finder.gym_finder.h1.heading')}
        </h1>
        <p style={{ color: 'var(--af-muted)', margin: '0 0 28px', fontSize: 15 }}>
          {translate('pages.public.gym_finder.gym_finder.p.book_gym_slot')}
        </p>

        <div style={{ position: 'relative', marginBottom: 28, color: 'var(--af-dim)' }}>
          <span
            style={{
              position: 'absolute',
              left: 14,
              top: '50%',
              transform: 'translateY(-50%)',
              pointerEvents: 'none',
            }}
          >
            <SearchIcon />
          </span>
          <input
            className="af-input"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={translate(
              'pages.public.gym_finder.gym_finder.input_input.placeholder_search',
            )}
            style={{ paddingLeft: 46 }}
          />
        </div>

        {(loading || membershipsLoading) && (
          <div
            className="af-card"
            style={{
              padding: 28,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              color: 'var(--af-muted)',
            }}
          >
            <Spinner />
            {translate('pages.public.gym_finder.gym_finder.div_card.loading_gym')}
          </div>
        )}

        {error && (
          <div className="af-error">
            {translate('pages.public.gym_finder.gym_finder.div_error.error_gym')} {error}
          </div>
        )}

        {!loading && !membershipsLoading && !error && (
          <>
            {SHOW_GYM_MAP && filtered.length > 0 && <GymMap gyms={filtered} />}

            <div className="af-gym-list">
              {filtered.map((gym, index) => (
                <GymCard
                  key={gym.id}
                  gym={gym}
                  index={index}
                  hasMembership={membershipGymIds.has(gym.id)}
                />
              ))}
              {filtered.length === 0 && (
                <div
                  className="af-card"
                  style={{ padding: '40px 0', textAlign: 'center', color: 'var(--af-muted)' }}
                >
                  {translate('pages.public.gym_finder.gym_finder.div_card.copy')}
                </div>
              )}
            </div>
          </>
        )}
      </section>
    </main>
  );
}

function GymCard({
  gym,
  index,
  hasMembership,
}: {
  gym: Gym;
  index: number;
  hasMembership: boolean;
}) {
  const destination = `/gyms/${gym.id}`;
  const rowStyle = {
    '--af-gym-row-delay': `${index * 70}ms`,
  } as CSSProperties;

  return (
    <article className="af-card af-gym-row-card" style={rowStyle}>
      <Link to={destination} className="af-gym-row">
        <div>
          <div
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              marginBottom: 7,
              flexWrap: 'wrap',
            }}
          >
            <span style={{ fontWeight: 800, fontSize: 17 }}>{gym.name}</span>
            {hasMembership && (
              <Tag>{translate('pages.public.gym_finder.gym_card.tag.membership')}</Tag>
            )}
          </div>
          <div
            style={{
              color: 'var(--af-muted)',
              fontSize: 13,
              display: 'flex',
              gap: 16,
              flexWrap: 'wrap',
            }}
          >
            <span>
              {gym.addressLine1}
              {','} {gym.city}
            </span>
          </div>
        </div>
        <span className="af-gym-row-arrow" aria-hidden="true" />
      </Link>
      <div className="af-gym-row-disclosure">
        <GymInfoDisclosure gym={gym} />
      </div>
    </article>
  );
}

function GymMap({ gyms }: { gyms: Gym[] }) {
  const navigate = useNavigate();
  const mapRef = useRef<HTMLDivElement>(null);
  const [mapError, setMapError] = useState<string | null>(null);
  const mappedGyms = useMemo(
    () =>
      gyms.flatMap((gym) => {
        const coordinates = gymCoordinates(gym);
        return coordinates ? [{ ...gym, mapLat: coordinates.lat, mapLng: coordinates.lng }] : [];
      }),
    [gyms],
  );

  useEffect(() => {
    const apiKey = getRuntimeConfig('VITE_GOOGLE_MAPS_API_KEY');
    if (!mapRef.current || mappedGyms.length === 0) return;
    if (!apiKey) {
      setMapError(translate('pages.public.gym_finder.gym_map.set_map_error.map'));
      return;
    }

    let cancelled = false;
    const listeners: Array<{ remove: () => void }> = [];
    const markers: GoogleMapMarker[] = [];
    setMapError(null);

    void loadGoogleMaps(apiKey)
      .then(({ Map, Marker, LatLngBounds }) => {
        if (cancelled || !mapRef.current) return;

        const firstGym = mappedGyms[0]!;
        const map = new Map(mapRef.current, {
          center: { lat: firstGym.mapLat, lng: firstGym.mapLng },
          zoom: 13,
          disableDefaultUI: false,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
        });
        const bounds = new LatLngBounds();

        for (const gym of mappedGyms) {
          const position = { lat: gym.mapLat, lng: gym.mapLng };
          bounds.extend(position);
          const marker = new Marker({
            map,
            position,
            title: gym.name,
          });
          markers.push(marker);
          listeners.push(marker.addListener('click', () => navigate(`/gyms/${gym.id}`)));
        }

        if (mappedGyms.length === 1) {
          map.setCenter({ lat: firstGym.mapLat, lng: firstGym.mapLng });
          map.setZoom(14);
        } else {
          map.fitBounds(bounds, 56);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setMapError(
            err instanceof Error
              ? err.message
              : translate('pages.public.gym_finder.gym_map.set_map_error.error_map'),
          );
        }
      });

    return () => {
      cancelled = true;
      listeners.forEach((listener) => listener.remove());
      markers.forEach((marker) => marker.setMap(null));
    };
  }, [mappedGyms, navigate]);

  if (mappedGyms.length === 0) {
    return (
      <div className="af-card af-gym-map-empty">
        {translate('pages.public.gym_finder.gym_map.div_card_gym_map_empty.gym_map_save')}
      </div>
    );
  }

  return (
    <div
      className="af-gym-map"
      aria-label={translate('pages.public.gym_finder.gym_map.div_gym_map.aria_label_map')}
      ref={mapRef}
    >
      {mapError && <div className="af-gym-map-error">{mapError}</div>}
    </div>
  );
}

function gymCoordinates(gym: Gym) {
  const lat = Number(gym.lat);
  const lng = Number(gym.lng);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
}

function loadGoogleMaps(apiKey: string) {
  const existingGoogle = (window as unknown as { google?: { maps?: GoogleMapsApi } }).google;
  if (existingGoogle?.maps?.Map && existingGoogle.maps.Marker && existingGoogle.maps.LatLngBounds) {
    return Promise.resolve(existingGoogle.maps);
  }
  if (googleMapsPromise) return googleMapsPromise;

  googleMapsPromise = new Promise<GoogleMapsApi>((resolve, reject) => {
    const callbackName = `__atomfitPublicGoogleMapLoaded_${Date.now()}`;
    const callbacks = window as unknown as Window & Record<string, (() => void) | undefined>;
    const existingScript = document.querySelector<HTMLScriptElement>(
      'script[data-atomfit-google-maps]',
    );

    function resolveMaps() {
      const google = (window as unknown as { google?: { maps?: GoogleMapsApi } }).google;
      if (google?.maps?.Map && google.maps.Marker && google.maps.LatLngBounds) {
        resolve(google.maps);
        return;
      }
      reject(new Error('Google Maps JavaScript API is unavailable.'));
    }

    if (existingScript) {
      existingScript.addEventListener('load', resolveMaps, { once: true });
      existingScript.addEventListener(
        'error',
        () => reject(new Error('Google Maps failed to load.')),
        { once: true },
      );
      resolveMaps();
      return;
    }

    callbacks[callbackName] = () => {
      delete callbacks[callbackName];
      resolveMaps();
    };

    const script = document.createElement('script');
    script.dataset['atomfitGoogleMaps'] = 'public-map';
    script.async = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&v=weekly&loading=async&callback=${callbackName}`;
    script.onerror = () => {
      delete callbacks[callbackName];
      reject(new Error('Google Maps failed to load.'));
    };
    document.head.appendChild(script);
  });

  return googleMapsPromise;
}
